import React from 'react';

import { css } from '@emotion/css';
import { Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { TextToast } from '@/components/toast/TextToast.tsx';
import { useStyles } from '@/hooks/useStyles';
import {
  AlertBGMPeriod,
  AlertConfig,
  HypoglycemiaAlertParametersBGM,
  alertCategoriesTranslations,
} from '@/models/AlertModel.ts';
import { HappenedInTheLastXDays } from '@/pages/patient-settings/alert-settings/inputs/HappenedInTheLastXDays.tsx';
import { NumberOfOccurrences } from '@/pages/patient-settings/alert-settings/inputs/NumberOfOccurrences.tsx';
import { SelectType } from '@/pages/patient-settings/alert-settings/inputs/SelectType.tsx';
import { Queries } from '@/queries/Queries.ts';
import { formatNumber } from '@/utils/mathUtils.ts';

export type AlertHypoglycemiaBGMSettingsProps = {
  patientId: string;
  alertConfig: AlertConfig;
  parameters: HypoglycemiaAlertParametersBGM;
};

export const AlertHypoglycemiaBGMSettings: React.FC<
  AlertHypoglycemiaBGMSettingsProps
> = ({ parameters, patientId, alertConfig }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const editAlertConfig = Queries.alerts.useEditAlertConfig();

  const updateParameters = (
    value: unknown,
    key: keyof HypoglycemiaAlertParametersBGM,
  ) => {
    editAlertConfig.mutate(
      {
        patientId,
        alertConfig: {
          id: alertConfig.id,
          parameters: { ...parameters, [key]: value },
        },
      },
      {
        onSuccess: () =>
          TextToast.success(
            t('alertConfig.conditionsSuccess', {
              alert: t(alertCategoriesTranslations.hypoglycemia),
            }),
          ),
        onError: () =>
          TextToast.error(
            t('alertConfig.conditionsError', {
              alert: t(alertCategoriesTranslations.hypoglycemia),
            }),
          ),
      },
    );
  };

  return (
    <div className={styles.container}>
      <NumberOfOccurrences
        textBefore={t('alertConfig.moreThan')}
        textAfter={t('glycemia.hypoglycemia', {
          count: parameters.max_number_of_events,
        })}
        onCountChange={count => updateParameters(count, 'max_number_of_events')}
        minCount={1}
        maxCount={999}
        count={formatNumber(parameters.max_number_of_events, 0)}
      />
      <SelectType<AlertBGMPeriod>
        onSelectChange={value => updateParameters(value, 'periods')}
        options={[
          {
            value: 'all',
            text: t('alertConfig.periods.all'),
          },
          {
            value: 'fasting',
            text: t('alertConfig.periods.fasting'),
          },
          {
            value: 'before_meal',
            text: t('alertConfig.periods.before_meal'),
          },
          {
            value: 'after_meal',
            text: t('alertConfig.periods.after_meal'),
          },
        ]}
        selected={parameters.periods}
      />
      <SelectType
        onSelectChange={type => updateParameters(type, 'severe_only')}
        options={[
          { value: false, text: t('alertConfig.lowOrVeryLow') },
          { value: true, text: t('alertConfig.veryLow') },
        ]}
        selected={parameters.severe_only}
      />
      <HappenedInTheLastXDays
        onDaysChange={days => updateParameters(days, 'number_of_days')}
        minDays={1}
        maxDays={365}
        days={parameters.number_of_days}
      />
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
});
